import React, { useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import { Button } from './ui/button';
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, Heading1, Heading2,
  Quote, Highlighter, Save, Loader2
} from 'lucide-react';

export default function RichTextEditor({ content, onSave, saving, minimal = false }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight,
    ],
    content: content || '',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[80px]',
      },
    },
  });

  const handleSave = useCallback(() => {
    if (editor) {
      onSave(editor.getHTML());
    }
  }, [editor, onSave]);

  if (!editor) return null;

  const ToolButton = ({ onClick, active, icon: Icon, title }) => (
    <Button
      variant="ghost"
      size="icon"
      className={`h-7 w-7 ${active ? 'bg-muted' : ''}`}
      onClick={onClick}
      title={title}
      type="button"
    >
      <Icon size={14} />
    </Button>
  );

  return (
    <div className="tiptap-editor flex-1">
      {!minimal && (
        <div className="flex flex-wrap items-center gap-0.5 p-2 border-b bg-muted/30 rounded-t-lg">
          <ToolButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} icon={Heading1} title="Heading 1" />
          <ToolButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} icon={Heading2} title="Heading 2" />
          <div className="w-px h-5 bg-border mx-1" />
          <ToolButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} icon={Bold} title="Bold" />
          <ToolButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} icon={Italic} title="Italic" />
          <ToolButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} icon={UnderlineIcon} title="Underline" />
          <ToolButton onClick={() => editor.chain().focus().toggleHighlight().run()} active={editor.isActive('highlight')} icon={Highlighter} title="Highlight" />
          <div className="w-px h-5 bg-border mx-1" />
          <ToolButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} icon={List} title="Bullet List" />
          <ToolButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} icon={ListOrdered} title="Numbered List" />
          <ToolButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} icon={Quote} title="Quote" />
          <div className="w-px h-5 bg-border mx-1" />
          <ToolButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} icon={AlignLeft} title="Align Left" />
          <ToolButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} icon={AlignCenter} title="Center" />
          <ToolButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} icon={AlignRight} title="Align Right" />
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={handleSave} disabled={saving} className="h-7">
            {saving ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Save size={12} className="mr-1" />}
            Save
          </Button>
        </div>
      )}
      <div className={`border ${minimal ? 'rounded-lg' : 'rounded-b-lg'} bg-white`}>
        <EditorContent editor={editor} className="p-3" />
      </div>
      {minimal && (
        <div className="flex justify-end mt-1">
          <Button size="sm" variant="ghost" onClick={handleSave} disabled={saving} className="h-6 text-xs">
            {saving ? <Loader2 size={10} className="mr-1 animate-spin" /> : <Save size={10} className="mr-1" />}
            Save
          </Button>
        </div>
      )}
    </div>
  );
}
